import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  LineChart,
  Line
} from 'recharts';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import ArrowIcon from '../../../shared/assets/svg/arrow-large-right.svg';
import {
  getAllSalesTargetsByProjectId,
  getAllSalesUsersByProjectId,
  getConversionStatsByProjectId,
  getFunnelStatsByProjectId,
  getSalesCallsStatsByProjectId,
  getSalesCycleLengthByProjectId,
  getYearlyConversionStatsByProjectId
} from '../../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';

const SalesGraphs = ({ projectID, month }) => {
  const { register: formRegister, watch } = useForm();
  const userID = Number(Cookies.get('userId'));
  const navigate = useNavigate();

  // State management
  const [showIndividual, setShowIndividual] = useState(false);
  const [showARIndividual, setShowARIndividual] = useState(false);
  const [showNBIndividual, setShowNBIndividual] = useState(false);
  const [showCSIndividual, setShowCSIndividual] = useState(false);
  const [leadViewMode, setLeadViewMode] = useState('funnel');
  const [designation, setDesignation] = useState('');
  const [week_nos, setWeek_nos] = useState('');

  useEffect(() => {
    setDesignation(Cookies.get('userDesignationLevel'));
  }, []);

  const {
    data: allSales,
    refetch: refetchSales,
    isFetching
  } = useQuery({
    queryKey: ['sales', projectID],
    queryFn: () => getAllSalesUsersByProjectId(projectID, 1, 1000),
    enabled: !!projectID
  });

  const { data: allTargets, refetch: refetTargets } = useQuery({
    queryKey: ['allTargets', projectID, month],
    queryFn: () => getAllSalesTargetsByProjectId(projectID, month),
    enabled: !!projectID && !!month, // only runs when both are truthy
    onSuccess: (data) => {
      mixpanel.track('Sales Targets API Success', {
        user_id: userID,
        project_id: projectID,
        month: month,
        targets_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Sales Targets API Failed', {
        user_id: userID,
        project_id: projectID,
        month: month,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: funnelStats,
    refetch: refetchFunnelStats,
    isFetching: isFetchingFunnelStats
  } = useQuery({
    queryKey: ['funnelStats', projectID],
    queryFn: () => getFunnelStatsByProjectId(projectID),
    enabled: !!projectID,
    onSuccess: (data) => {
      mixpanel.track('Funnel Stats API Success', {
        user_id: userID,
        project_id: projectID,
        total_leads: Object.values(data?.data || {}).reduce((sum, val) => sum + (val || 0), 0),
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Funnel Stats API Failed', {
        user_id: userID,
        project_id: projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: salesCycleLength,
    refetch: refetchSalesCycleLength,
    isFetching: isFetchingSalesCycleLength
  } = useQuery({
    queryKey: ['salesCycleLength', projectID],
    queryFn: () => getSalesCycleLengthByProjectId(projectID),
    enabled: !!projectID
  });

  const { data: salesCallsStats, refetch: refetchSalesCallsStats } = useQuery({
    queryKey: ['salesCallsStats', projectID, week_nos],
    queryFn: () => getSalesCallsStatsByProjectId(projectID, week_nos),
    enabled: !!projectID
  });

  const { data: yearlyConversionStats, refetch: refetchYearlyConversionStats } = useQuery({
    queryKey: ['yearlyConversionStats', projectID, month],
    queryFn: () => getYearlyConversionStatsByProjectId(projectID, month),
    enabled: !!projectID && !!month // only runs when both are truthy
  });

  const { data: conversionStats, refetch: refetchConversionStats } = useQuery({
    queryKey: ['conversionStats', projectID, month],
    queryFn: () => getConversionStatsByProjectId(projectID, month),
    enabled: !!projectID && !!month // only runs when both are truthy
  });

  // Constants
  const COLORS = ['#FFA8A1', '#A194FF'];

  // Data processing functions
  const getPercentage = (achieved, target) =>
    target > 0 ? Math.min((achieved / target) * 100, 100) : 0;

  // Get current user data
  const midGuy = allTargets?.data?.find((item) => item?.sales_user_id === userID);

  // Performance data
  const individualData = [
    {
      name: 'Performance',
      NB_Percent: getPercentage(midGuy?.booking_achieved || 0, midGuy?.target_amount || 0),
      AR_Percent: getPercentage(midGuy?.monthly_target || 0, midGuy?.ar_target || 0)
    }
  ];

  const teamData = allTargets?.data
    ?.filter((item) => item?.sales_user_id !== midGuy?.sales_user_id)
    .map((item) => ({
      name: item?.user?.name || '',
      NB_Percent: getPercentage(item?.booking_achieved || 0, item?.target_amount || 0),
      AR_Percent: getPercentage(item?.monthly_target || 0, item?.ar_target || 0)
    }));

  const showNB = individualData.some((d) => d.NB_Percent !== undefined);
  const showAR = individualData.some((d) => d.AR_Percent !== undefined);

  // Funnel data
  const leadFunnelData = [
    { name: 'Cold', value: funnelStats?.data?.cold || 0, color: '#7FB8E2' },
    { name: 'Warm', value: funnelStats?.data?.warm || 0, color: '#A7D3F5' },
    { name: 'Hot', value: funnelStats?.data?.Hot || 0, color: '#FFA07A' },
    { name: 'Confirmed', value: funnelStats?.data?.confirmed || 0, color: '#FF7F50' },
    { name: 'Closed Won', value: funnelStats?.data?.closedWon || 0, color: '#FF4500' },
    { name: 'Closed Lost', value: funnelStats?.data?.closedLost || 0, color: '#9E9E9E' }
  ];

  // AR and NB Pie data
  const sampleData = {
    team:
      typeof midGuy?.monthly_target === 'number' || typeof midGuy?.ar_target === 'number'
        ? [
            {
              name: 'Team',
              collected: typeof midGuy?.monthly_target === 'number' ? midGuy.monthly_target : 0,
              target: typeof midGuy?.ar_target === 'number' ? midGuy.ar_target : 0
            }
          ]
        : [],
    individual: (allTargets?.data || [])
      .filter(
        (item) =>
          (typeof item?.monthly_target === 'number' || typeof item?.ar_target === 'number') &&
          item?.sales_user_id !== midGuy?.sales_user_id
      )
      .map((item) => ({
        name: item?.user?.name || 'Unknown',
        collected: typeof item?.monthly_target === 'number' ? item.monthly_target : 0,
        target: typeof item?.ar_target === 'number' ? item.ar_target : 0
      }))
  };

  const ARPieData = showARIndividual ? sampleData.individual : sampleData.team;

  const NBData = {
    team:
      typeof midGuy?.target_amount === 'number' || typeof midGuy?.booking_achieved === 'number'
        ? [
            {
              name: 'Team',
              target: typeof midGuy?.target_amount === 'number' ? midGuy.target_amount : 0,
              achieved: typeof midGuy?.booking_achieved === 'number' ? midGuy.booking_achieved : 0
            }
          ]
        : [],
    individual: (allTargets?.data || [])
      .filter(
        (item) =>
          (typeof item?.target_amount === 'number' || typeof item?.booking_achieved === 'number') &&
          item?.sales_user_id !== midGuy?.sales_user_id
      )
      .map((item) => ({
        name: item?.user?.name || 'Unknown',
        target: typeof item?.target_amount === 'number' ? item.target_amount : 0,
        achieved: typeof item?.booking_achieved === 'number' ? item.booking_achieved : 0
      }))
  };

  const NBPieData = showNBIndividual ? NBData.individual : NBData.team;

  // Conversion Status data
  const sampleCSData = {
    team: [
      {
        name: conversionStats?.data?.name,
        converted: conversionStats?.data?.converted || 0,
        target: conversionStats?.data?.total || 0
      }
    ],
    individual:
      conversionStats?.data?.users
        ?.filter((item) => item?.leadsConverted || item?.leadsAdded)
        .map((item) => ({
          name: item?.name,
          converted: item?.leadsConverted || 0,
          target: item?.leadsAdded || 0
        })) || []
  };

  const currenCStData = showCSIndividual ? sampleCSData.individual : sampleCSData.team;

  // Sales Cycle Length data
  const SCLDemoData = [
    { stage: 'Warm', days: salesCycleLength?.worm || 0, color: '#8979FF' },
    { stage: 'Hot', days: salesCycleLength?.hot || 0, color: '#8979FF' },
    { stage: 'Confirmed', days: salesCycleLength?.confirmed || 0, color: '#8979FF' },
    { stage: 'Closed Won', days: salesCycleLength?.closedWon || 0, color: '#8979FF' }
  ];

  // Yearly Conversion data
  const yearType = watch('year_type');
  const conversionType = watch('conversion_type');
  const fullYearData =
    yearlyConversionStats?.data?.map((item) => {
      const baseData = { month: item?.monthName?.substring(0, 3) };
      if (conversionType === 'team') {
        return {
          ...baseData,
          leadsCreated: item?.total || 0,
          leadsConverted: item?.converted || 0
        };
      } else {
        const selectedUserData = item?.users?.find((user) => user.id === Number(conversionType));
        return {
          ...baseData,
          leadsCreated: selectedUserData?.leadsAdded || 0,
          leadsConverted: selectedUserData?.leadsConverted || 0
        };
      }
    }) || [];

  const halfYearData = fullYearData.slice(0, 6);
  const chartData = yearType === 'yearly' ? fullYearData : halfYearData;

  // Pie data functions
  const getNBPieData = (agent) => {
    const toBeCollected = agent.target - agent.achieved;
    return [
      { name: 'Target', value: toBeCollected },
      { name: 'Achieved', value: agent.achieved }
    ];
  };

  const getARPieData = (agent) => {
    const toBeCollected = agent.target - agent.collected;
    return [
      { name: 'To Be Collected', value: toBeCollected },
      { name: 'Collected', value: agent.collected }
    ];
  };

  const getCSPieData = (agent) => {
    const toBeconverted = agent.target - agent.converted;
    return [
      { name: 'Yet To Convert', value: toBeconverted },
      { name: 'Converted', value: agent.converted }
    ];
  };

  // Chart component functions
  const CustomPercentageLabel = ({ x, y, width, value }) => {
    if (!value) return null;
    return (
      <text
        x={x + width / 2}
        y={y + 15}
        fill="white"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="12"
        fontWeight="bold">
        {`${value.toFixed(0)}%`}
      </text>
    );
  };

  const TopRoundedBar1 = (props) => {
    const { x, y, width, height, fill, payload } = props;
    if (payload.value === 0) return null;
    const radius = 6;
    return (
      <path
        d={`
          M${x},${y + height}
          V${y + radius}
          Q${x},${y} ${x + radius},${y}
          H${x + width - radius}
          Q${x + width},${y} ${x + width},${y + radius}
          V${y + height}
          Z
        `}
        style={{ cursor: 'pointer' }}
        fill={fill}
      />
    );
  };

  const renderCustomLabelForPie = ({
    value,
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index
  }) => {
    if (value === 0) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 12, fontWeight: 500 }}>
        {value}
      </text>
    );
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Event handlers
  const handleBarClick = (data) => {
    if (data?.name) {
      mixpanel.track('Sales Funnel Bar Clicked', {
        user_id: userID,
        status: data.name,
        value: data.value,
        project_id: projectID,
        timestamp: new Date().toISOString()
      });
      navigate('/builder/sales/', { state: { secondaryTab: 'Leads', status: data.name } });
    }
  };

  const handlePerformanceToggle = () => {
    const newValue = !showIndividual;
    setShowIndividual(newValue);
    mixpanel.track('Performance View Toggle', {
      user_id: userID,
      view_type: newValue ? 'Individual' : 'Team',
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  const handleNBToggle = () => {
    const newValue = !showNBIndividual;
    setShowNBIndividual(newValue);
    mixpanel.track('New Bookings View Toggle', {
      user_id: userID,
      view_type: newValue ? 'Individual' : 'Team',
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  const handleARToggle = () => {
    const newValue = !showARIndividual;
    setShowARIndividual(newValue);
    mixpanel.track('Amount Receivables View Toggle', {
      user_id: userID,
      view_type: newValue ? 'Individual' : 'Team',
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  const handleCSToggle = () => {
    const newValue = !showCSIndividual;
    setShowCSIndividual(newValue);
    mixpanel.track('Conversion Status View Toggle', {
      user_id: userID,
      view_type: newValue ? 'Individual' : 'Team',
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  const handleWeekFilterChange = (value) => {
    setWeek_nos(value);
    mixpanel.track('Call Performance Week Filter Changed', {
      user_id: userID,
      week: value,
      project_id: projectID,
      timestamp: new Date().toISOString()
    });
  };

  // Deals Target metrics
  const metrics = [
    {
      title: 'New Bookings',
      achieved: midGuy?.booking_achieved || 0,
      target: midGuy?.target_amount || 0,
      color: 'bg-[#FFA8A1]'
    },
    {
      title: 'Amount Receivable',
      achieved: midGuy?.monthly_target || 0,
      target: midGuy?.ar_target || 0,
      color: 'bg-[#a194ff]'
    }
  ];

  return (
    <div className="flex flex-col gap-5 transition-all duration-300 ease-in-out w-full justify-center items-center">
      {/* Deals Target — one card per metric */}
      <div className="grid w-full max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2">
        {metrics.map((item, index) => {
          const safeTarget = Number(item.target) || 0;
          const safeAchieved = Number(item.achieved) || 0;
          const percentage =
            safeTarget > 0 ? Math.min((safeAchieved / safeTarget) * 100, 100) : 0;
          return (
            <div
              key={index}
              className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="mb-3 text-sm font-semibold text-gray-800">{item.title}</p>
              <div className="relative mb-2 h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-2 rounded-full ${item.color}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="mt-auto flex justify-between text-xs text-gray-600">
                <span>₹{safeAchieved.toLocaleString('en-IN')} INR</span>
                <span>₹{safeTarget.toLocaleString('en-IN')} INR</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex w-full max-w-4xl items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-800 shadow-sm sm:text-lg">
        <span>Incentive achieved</span>
        <img className="h-8 w-12 shrink-0" src={ArrowIcon} alt="" />
        <strong>
          ₹
          {(
            Number(midGuy?.bookingincentive_achieved || 0) +
            Number(midGuy?.achived_ar_incentive || 0)
          ).toLocaleString('en-IN')}
        </strong>
      </div>

      {/* Sales Funnel / Lead Status */}
      <div className="p-4 w-full max-w-4xl mx-auto bg-white rounded-xl flex flex-col justify-start items-center shadow gap-3">
        <div className="w-full flex flex-col items-center justify-between gap-3 sm:flex-row sm:items-center">
          <h3 className="text-lg font-bold text-[#346C75] tracking-wide uppercase">
            Lead Journey
          </h3>
          <div className="grid w-full max-w-sm grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1 sm:w-auto sm:min-w-[290px]">
            <button
              type="button"
              onClick={() => setLeadViewMode('funnel')}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                leadViewMode === 'funnel'
                  ? 'bg-white text-[#0A4A5C] shadow'
                  : 'text-gray-600 hover:bg-white/70'
              }`}>
              Sales Funnel
            </button>
            <button
              type="button"
              onClick={() => setLeadViewMode('status')}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                leadViewMode === 'status'
                  ? 'bg-white text-[#0A4A5C] shadow'
                  : 'text-gray-600 hover:bg-white/70'
              }`}>
              Lead Status
            </button>
          </div>
        </div>
        <div className="bg-black h-[2px] w-[80%] mb-1" />
        <div className="w-full flex flex-col items-center">
          {leadFunnelData.some((item) => item.value > 0) ? (
            leadViewMode === 'funnel' ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={leadFunnelData.map((item) => ({
                    ...item,
                    displayValue: leadFunnelData.some((d) => d.value >= 50)
                      ? Math.min(item.value, 50)
                      : item.value,
                    actualValue: item.value
                  }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    type="number"
                    domain={leadFunnelData.some((d) => d.value >= 50) ? [0, 50] : [0, 'dataMax']}
                    allowDataOverflow={false}
                    allowDecimals={false}
                  />
                  <Bar
                    dataKey="displayValue"
                    shape={<TopRoundedBar1 />}
                    onClick={handleBarClick}
                    style={{ cursor: 'pointer' }}
                    isAnimationActive={false}>
                    {leadFunnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="actualValue"
                      content={({ x, y, width, height, value }) => {
                        if (value === 0) return null;
                        const exceedsMax = leadFunnelData.some((d) => d.value >= 50) && value > 50;
                        return (
                          <text
                            x={x + width / 2}
                            y={exceedsMax ? y + height / 2 : y + 15}
                            textAnchor="middle"
                            style={{
                              fontSize: 12,
                              fill: '#fff',
                              fontWeight: 500
                            }}>
                            {value}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <>
                <div className="flex justify-center mb-6">
                  <PieChart width={240} height={240}>
                    <Pie
                      data={leadFunnelData}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="value"
                      label={renderCustomLabelForPie}
                      labelLine={false}>
                      {leadFunnelData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </div>

                <div className="flex gap-6 flex-wrap justify-center">
                  {leadFunnelData.map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: entry.color }}></div>
                      <span className="text-xs text-gray-700">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )
          ) : (
            <div className="text-sm text-gray-500 mt-4 h-[10rem]">No data available</div>
          )}
        </div>
      </div>

      {/* Performance Graph */}
      {designation !== 'Low' && (
        <div className="p-4 w-full max-w-4xl mx-auto bg-white rounded-xl flex flex-col justify-start items-center shadow gap-3">
          <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
            Performance Graph
          </h3>
          <div className="bg-black h-[2px] w-[80%] mb-4" />

          <div className="flex justify-between w-full px-10 mb-4">
            <div className="flex gap-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#FFA8A1] rounded-sm"></div>
                <span className="text-xs">New Bookings (%)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#A194FF] rounded-sm"></div>
                <span className="text-xs">Amount Receivable (%)</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span
                className={`font-semibold ${!showIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Team
              </span>
              <div
                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 bg-gray-300`}
                onClick={handlePerformanceToggle}>
                <div
                  className={`w-4 h-4 bg-[#346C75] rounded-full absolute top-0.5 transition-all duration-300 ${
                    showIndividual ? 'right-0.5' : 'left-0.5'
                  }`}></div>
              </div>
              <span
                className={`font-semibold ${showIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Individual
              </span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            {!showIndividual ? (
              individualData &&
              individualData.length > 0 &&
              (individualData[0].NB_Percent > 0 || individualData[0].AR_Percent > 0) ? (
                <BarChart data={individualData} barCategoryGap="20%" barGap={100}>
                  <XAxis dataKey="name" />
                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    label={{
                      value: 'Achieved (%)',
                      angle: -90,
                      position: 'insideLeft',
                      dy: -10
                    }}
                  />
                  {showNB && (
                    <Bar dataKey="NB_Percent" fill="#FFA8A1" radius={[10, 10, 0, 0]}>
                      <LabelList
                        dataKey="NB_Percent"
                        position="insideTop"
                        content={CustomPercentageLabel}
                        formatter={(value) => `${value}%`}
                      />
                    </Bar>
                  )}
                  {showAR && (
                    <Bar dataKey="AR_Percent" fill="#A194FF" radius={[10, 10, 0, 0]}>
                      <LabelList
                        dataKey="AR_Percent"
                        position="insideTop"
                        content={CustomPercentageLabel}
                        formatter={(value) => `${value}%`}
                      />
                    </Bar>
                  )}
                </BarChart>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                  No data available
                </div>
              )
            ) : teamData &&
              teamData.length > 0 &&
              teamData.some((d) => d.NB_Percent > 0 || d.AR_Percent > 0) ? (
              <BarChart data={teamData} barCategoryGap="30%" barGap={4}>
                <XAxis dataKey="name" />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  label={{
                    value: 'Achieved (%)',
                    angle: -90,
                    position: 'insideLeft',
                    dy: -10
                  }}
                />
                <Bar dataKey="NB_Percent" fill="#FFA8A1" radius={[10, 10, 0, 0]}>
                  <LabelList
                    dataKey="NB_Percent"
                    position="insideTop"
                    content={CustomPercentageLabel}
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
                <Bar dataKey="AR_Percent" fill="#A194FF" radius={[10, 10, 0, 0]}>
                  <LabelList
                    dataKey="AR_Percent"
                    position="insideTop"
                    content={CustomPercentageLabel}
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              </BarChart>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                No data available
              </div>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* New Bookings */}
      {designation !== 'Low' && (
        <div className="flex rounded-lg shadow bg-white gap-4 p-6 flex-col items-center w-full max-w-4xl ">
          <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
            New Bookings
          </h3>
          <div className="bg-black h-[2px] w-[80%] mb-4" />

          <div className="flex justify-between w-full max-w-4xl px-10 mb-4">
            <div className="flex gap-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#FFA8A1] rounded-sm"></div>
                <span className="text-xs">Target</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#A194FF] rounded-sm"></div>
                <span className="text-xs">Achieved</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs">
              <span
                className={`font-semibold ${!showNBIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Team
              </span>
              <div
                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 bg-gray-300`}
                onClick={handleNBToggle}>
                <div
                  className={`w-4 h-4 bg-[#346C75] rounded-full absolute top-0.5 transition-all duration-300 ${
                    showNBIndividual ? 'right-0.5' : 'left-0.5'
                  }`}></div>
              </div>
              <span
                className={`font-semibold ${showNBIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Individual
              </span>
            </div>
          </div>

          {NBPieData?.some((agent) => getNBPieData(agent).some((entry) => entry.value > 0)) ? (
            <div
              className={`w-full max-w-4xl ${
                showNBIndividual ? 'grid grid-cols-1 sm:grid-cols-2 gap-6' : 'flex justify-center'
              }`}>
              {NBPieData?.map((agent, index) => (
                <div key={index} className="flex flex-col items-center">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={getNBPieData(agent)}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={renderCustomLabelForPie}
                      labelLine={false}>
                      {getNBPieData(agent).map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx]} />
                      ))}
                    </Pie>
                  </PieChart>
                  <div className="text-sm text-center mt-1 font-medium text-[#346C75]">
                    {agent.name} (Target - {agent.target})
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 mt-4 h-[10rem]">No booking data available</div>
          )}
        </div>
      )}

      {/* Amount Receivables */}
      {designation !== 'Low' && (
        <div className="flex rounded-lg shadow bg-white gap-4 p-6 flex-col items-center w-full max-w-4xl ">
          <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
            Amount Receivables
          </h3>
          <div className="bg-black h-[2px] w-[80%] mb-4" />

          <div className="flex justify-between w-full max-w-4xl px-10 mb-4">
            <div className="flex gap-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#FFA8A1] rounded-sm"></div>
                <span className="text-xs">To Be Collected</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#A194FF] rounded-sm"></div>
                <span className="text-xs">Collected</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs">
              <span
                className={`font-semibold ${!showARIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Team
              </span>
              <div
                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 bg-gray-300`}
                onClick={handleARToggle}>
                <div
                  className={`w-4 h-4 bg-[#346C75] rounded-full absolute top-0.5 transition-all duration-300 ${
                    showARIndividual ? 'right-0.5' : 'left-0.5'
                  }`}></div>
              </div>
              <span
                className={`font-semibold ${showARIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Individual
              </span>
            </div>
          </div>

          {ARPieData?.some((agent) => getARPieData(agent).some((entry) => entry.value > 0)) ? (
            <div
              className={`w-full max-w-4xl ${
                showARIndividual ? 'grid grid-cols-1 sm:grid-cols-2 gap-6' : 'flex justify-center'
              }`}>
              {ARPieData.map((agent, index) => (
                <div key={index} className="flex flex-col items-center">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={getARPieData(agent)}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={renderCustomLabelForPie}
                      labelLine={false}>
                      {getARPieData(agent).map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx]} />
                      ))}
                    </Pie>
                  </PieChart>
                  <div className="text-sm text-center mt-1 font-medium text-[#346C75]">
                    {agent.name} (Target - {agent.target})
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 mt-4 h-[10rem]">
              No amount receivables data available
            </div>
          )}
        </div>
      )}

      {/* Call Performance Graph */}
      {designation !== 'Low' && (
        <div className="p-4 w-full max-w-4xl mx-auto bg-white rounded-xl flex flex-col justify-start items-center shadow gap-3">
          <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
            Call Performance Graph
          </h3>
          <div className="bg-black h-[2px] w-[80%] mb-4" />

          <div className="flex justify-between w-full px-10 mb-4">
            <FormInput
              width={20}
              register={formRegister}
              register_key={'week_filter'}
              label={''}
              type={'dropdown'}
              isRequired={false}
              options={[
                { value: '', label: 'All Weeks', selected: true },
                { value: '1', label: 'First Week' },
                { value: '2', label: 'Second Week' },
                { value: '3', label: 'Third Week' },
                { value: '4', label: 'Fourth Week' }
              ]}
              onDropdownChange={handleWeekFilterChange}
              valuePre={week_nos}
            />
            <div className="flex items-center gap-2 text-xs">
              <span
                className={`font-semibold ${!showIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Team
              </span>
              <div
                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 bg-gray-300`}
                onClick={handlePerformanceToggle}>
                <div
                  className={`w-4 h-4 bg-[#346C75] rounded-full absolute top-0.5 transition-all duration-300 ${
                    showIndividual ? 'right-0.5' : 'left-0.5'
                  }`}></div>
              </div>
              <span
                className={`font-semibold ${showIndividual ? 'text-[#346C75]' : 'text-gray-500'}`}>
                Individual
              </span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={showIndividual ? 400 : 120}>
            {(() => {
              const callsData = salesCallsStats?.data || [];
              const maxValue = Math.max(...callsData.map((item) => item.callsAssigned || 0), 10);

              if (!showIndividual) {
                const totalAssigned = callsData.reduce(
                  (sum, item) => sum + (item.callsAssigned || 0),
                  0
                );
                const totalCompleted = callsData.reduce(
                  (sum, item) => sum + (item.callsCompleted || 0),
                  0
                );
                const totalRemaining = totalAssigned - totalCompleted;
                const completedPercent =
                  totalAssigned > 0 ? (totalCompleted / totalAssigned) * 100 : 0;
                const remainingPercent =
                  totalAssigned > 0 ? (totalRemaining / totalAssigned) * 100 : 0;

                const teamData = [
                  {
                    name: 'Team',
                    completedPercent,
                    remainingPercent,
                    completedActual: totalCompleted,
                    remainingActual: totalRemaining,
                    totalActual: totalAssigned
                  }
                ];

                return (
                  <BarChart
                    layout="vertical"
                    data={teamData}
                    margin={{ top: 20, right: 50, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} hide />
                    <YAxis type="category" dataKey="name" width={120} />
                    <Bar
                      dataKey="completedPercent"
                      stackId="a"
                      fill="#8979FF"
                      radius={[0, 0, 0, 0]}>
                      <LabelList
                        dataKey="completedActual"
                        position="inside"
                        fill="#fff"
                        fontSize={13}
                        fontWeight="bold"
                        formatter={(value) => (value > 0 ? value : '')}
                      />
                    </Bar>
                    <Bar
                      dataKey="remainingPercent"
                      stackId="a"
                      fill="#D4CFFF"
                      radius={[0, 4, 4, 0]}>
                      <LabelList
                        dataKey="totalActual"
                        position="right"
                        fill="#374151"
                        fontSize={14}
                        fontWeight="bold"
                      />
                    </Bar>
                  </BarChart>
                );
              } else {
                const chartData = callsData.map((item) => {
                  const assigned = item.callsAssigned || 0;
                  const completed = item.callsCompleted || 0;
                  const remaining = assigned - completed;
                  const completedPercent = assigned > 0 ? (completed / assigned) * 100 : 0;
                  const remainingPercent = assigned > 0 ? (remaining / assigned) * 100 : 0;

                  return {
                    name: item.user.length > 18 ? item.user.substring(0, 18) + '...' : item.user,
                    fullName: item.user,
                    completedPercent,
                    remainingPercent,
                    completedActual: completed,
                    remainingActual: remaining,
                    totalActual: assigned
                  };
                });

                return chartData.length > 0 ? (
                  <BarChart
                    layout="vertical"
                    data={chartData}
                    margin={{ top: 20, right: 50, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} hide />
                    <YAxis type="category" dataKey="name" width={150} />
                    <Bar
                      dataKey="completedPercent"
                      stackId="a"
                      fill="#8979FF"
                      radius={[0, 0, 0, 0]}>
                      <LabelList
                        dataKey="completedActual"
                        position="inside"
                        fill="#fff"
                        fontSize={11}
                        formatter={(value) => (value > 0 ? value : '')}
                      />
                    </Bar>
                    <Bar
                      dataKey="remainingPercent"
                      stackId="a"
                      fill="#D4CFFF"
                      radius={[0, 4, 4, 0]}>
                      <LabelList
                        dataKey="totalActual"
                        position="right"
                        fill="#374151"
                        fontSize={12}
                        fontWeight="bold"
                      />
                    </Bar>
                  </BarChart>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                    No call data available
                  </div>
                );
              }
            })()}
          </ResponsiveContainer>

          <div className="flex gap-4">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#8979FF' }}></div>
              <span className="text-xs">Calls Completed</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#D4CFFF' }}></div>
              <span className="text-xs">Calls Assigned</span>
            </div>
          </div>
        </div>
      )}

      {/* Conversion Status */}
      {designation !== 'Low' && (
        <div className="flex rounded-lg shadow bg-white gap-4 p-6 flex-col items-center w-full max-w-4xl ">
          <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
            CONVERSION STATUS
          </h3>
          <div className="bg-black h-[2px] w-[80%] mb-4" />

          <div className="flex justify-between w-full max-w-4xl px-10 mb-4">
            <div className="flex gap-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#FFA8A1] rounded-sm"></div>
                <span className="text-xs">Yet To Convert</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-[#A194FF] rounded-sm"></div>
                <span className="text-xs">Converted</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs">
              <span
                className={`font-semibold ${!showCSIndividual ? 'text-[#346C75]' : 'text-black'}`}>
                Team
              </span>
              <div
                className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-300 ${
                  showCSIndividual ? 'bg-[#346C75]' : 'bg-gray-300'
                }`}
                onClick={handleCSToggle}>
                <div
                  className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-all duration-300 ${
                    showCSIndividual ? 'right-0.5' : 'left-0.5'
                  }`}></div>
              </div>
              <span className={`font-semibold ${showIndividual ? 'text-[#346C75]' : 'text-black'}`}>
                Individual
              </span>
            </div>
          </div>

          {currenCStData?.some((agent) => getCSPieData(agent).some((entry) => entry.value > 0)) ? (
            <div
              className={`w-full max-w-4xl ${
                showCSIndividual ? 'grid grid-cols-1 sm:grid-cols-2 gap-6' : 'flex justify-center'
              }`}>
              {currenCStData.map((agent, index) => (
                <div key={index} className="flex flex-col items-center">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={getCSPieData(agent)}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={renderCustomLabel}
                      labelLine={false}>
                      {getCSPieData(agent)?.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx]} />
                      ))}
                    </Pie>
                  </PieChart>
                  <div className="text-sm text-center mt-1 font-medium text-[#346C75]">
                    {agent.name} (Total Allocated - {agent.target})
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 mt-4 h-[10rem]">
              No conversion status data available
            </div>
          )}
        </div>
      )}

      {/* Sales Conversion */}
      {designation !== 'Low' && (
        <div className="flex flex-col rounded-lg shadow bg-white gap-4 p-4 items-center w-full max-w-4xl ">
          <h3 className="text-lg font-bold text-[#346C75] tracking-wide uppercase">
            SALES CONVERSION
          </h3>
          <div className="bg-black h-[2px] w-[80%]" />

          <div className="flex justify-center items-center w-full gap-4 mb-2">
            <FormInput
              width={20}
              register={formRegister}
              register_key={'conversion_type'}
              label={''}
              type={'dropdown'}
              isRequired={false}
              options={[
                { value: 'team', label: 'Team' },
                ...(allSales?.data
                  ?.filter((item) => item.id !== midGuy?.sales_user_id)
                  ?.map((item) => ({ value: item.id, label: item.name })) || [])
              ]}
              onType={(selected) => {
                // Track conversion type change
                mixpanel.track('Sales Conversion Filter Changed', {
                  user_id: userID,
                  filter_type: 'conversion_type',
                  new_value: selected?.value,
                  new_label: selected?.label,
                  project_id: projectID,
                  timestamp: new Date().toISOString()
                });
              }}
            />
            <FormInput
              width={20}
              register={formRegister}
              register_key={'year_type'}
              label={''}
              type={'dropdown'}
              isRequired={false}
              options={[
                { value: 'halfYearly', label: 'Half Yearly' },
                { value: 'yearly', label: 'Yearly' }
              ]}
              onType={(selected) => {
                // Track year type change
                mixpanel.track('Sales Conversion Filter Changed', {
                  user_id: userID,
                  filter_type: 'year_type',
                  new_value: selected?.value,
                  new_label: selected?.label,
                  project_id: projectID,
                  timestamp: new Date().toISOString()
                });
              }}
            />
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Line type="monotone" dataKey="leadsCreated" stroke="#A194FF" strokeWidth={3}>
                <LabelList dataKey="leadsCreated" position="top" fill="#000" fontSize={12} />
              </Line>
              <Line type="monotone" dataKey="leadsConverted" stroke="#FFA7A7" strokeWidth={3}>
                <LabelList dataKey="leadsConverted" position="top" fill="#000" fontSize={12} />
              </Line>
            </LineChart>
          </ResponsiveContainer>

          <div className="flex gap-6 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-3 bg-[#A194FF] rounded-sm" />
              <span className="text-xs">Leads Created</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-3 bg-[#FFA7A7] rounded-sm" />
              <span className="text-xs">Converted</span>
            </div>
          </div>
        </div>
      )}

      {/* Sales Cycle Length */}
      {designation !== 'Low' && (
        <div className="flex rounded-lg shadow bg-white gap-4 p-6 flex-col items-center w-full max-w-4xl ">
          <h3 className="text-lg font-bold mb-1 text-[#346C75] tracking-wide uppercase">
            SALES CYCLE LENGTH
          </h3>
          <div className="bg-black h-[2px] w-[80%] " />

          <div className="w-full max-w-4xl flex justify-center">
            <div className="w-full max-w-4xl mt-6">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  layout="vertical"
                  data={(() => {
                    const hasValueOver40 = SCLDemoData?.some((item) => item.days > 30);
                    const cappedData = SCLDemoData.map((item) => ({
                      ...item,
                      capped_days: hasValueOver40 && item.days > 30 ? 30 : item.days,
                      actual_days: item.days
                    }));
                    return cappedData;
                  })()}
                  margin={{ top: 5, right: 50, bottom: 5, left: 100 }}
                  barCategoryGap="20%"
                  barGap={10}>
                  <CartesianGrid
                    strokeDasharray="0"
                    horizontal={true}
                    vertical={true}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    type="number"
                    stroke="#000"
                    tick={{ fill: '#000' }}
                    tickFormatter={(value) => Math.floor(value).toString()}
                    domain={(() => {
                      const hasValueOver40 = SCLDemoData?.some((item) => item.days > 40);
                      return hasValueOver40 ? [0, 30] : [0, 4];
                    })()}
                  />
                  <YAxis type="category" dataKey="stage" stroke="#000" tick={{ fill: '#000' }} />
                  <Bar dataKey="capped_days" fill="#A194FF" radius={[0, 10, 10, 0]}>
                    <LabelList dataKey="actual_days" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-[#A194FF] rounded-sm"></div>
              <span className="text-xs">Days</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesGraphs;
